import type { Product, Region } from '@/pageTemplate/LemonadeGoesGlobalPageTemplate';
import type { Locale } from '@lemonade-hq/lemonation';

const productsEnTranslations = {
  homeowners: 'Homeowners',
  landlords: 'Landlords',
  buildings: 'Buildings',
  renters: 'Renters',
  contents: 'Contents',
  liability: 'Liability',
  contents_and_liability: 'Contents and Liability',
  pet: 'Pet',
  life: 'Life',
  car: 'Car',
};

export const TRANSLATED_PRODUCTS = {
  'en-US': productsEnTranslations,
  'en-GB': productsEnTranslations,
  'en-FR': productsEnTranslations,
  'en-NL': productsEnTranslations,
  'en-DE': productsEnTranslations,
  'es-US': productsEnTranslations,
  'de-DE': {
    homeowners: 'Hauseigentümer',
    landlords: 'Vermieter',
    buildings: 'Gebäude',
    renters: 'Mieter',
    contents: 'Hausrat',
    liability: 'Haftpflicht',
    contents_and_liability: 'Hausrat und Haftpflicht',
    pet: 'Haustiere',
    life: 'Lebensversicherung',
    car: 'Kfz',
  },
  'nl-NL': {
    homeowners: 'Huisbezitters',
    landlords: 'Verhuurders',
    buildings: 'Gebouwen',
    renters: 'Huurders',
    contents: 'Inboedel',
    liability: 'Aansprakelijkheid',
    contents_and_liability: 'Inboedel en Aansprakelijkheid',
    pet: 'Huisdieren',
    life: 'Levensverzekering',
    car: 'Auto',
  },
  'fr-FR': {
    homeowners: 'Propriétaires',
    landlords: 'Propriétaires',
    buildings: 'bâtiments',
    renters: 'Locataires',
    contents: 'Habitation',
    liability: 'Responsabilité civile',
    contents_and_liability: 'Habitation et Responsabilité Civile',
    pet: 'Animaux',
    life: 'Assurance vie temporaire',
    car: 'Auto',
  },
} as Record<Locale, Record<Product, string>>;

type Placeholder = {
  REGION: string;
  PRODUCT: string;
  LOCATION: Record<Region, string>;
  EMAIL: string;
};

const ENGLISH_PLACEHOLDERS = {
  REGION: 'REGION',
  PRODUCT: 'PRODUCT',
  LOCATION: {
    us: 'STATE',
    eu: 'COUNTRY',
  },
  EMAIL: 'MY EMAIL',
} as Placeholder;

export const PLACEHOLDERS = {
  'en-US': ENGLISH_PLACEHOLDERS,
  'en-GB': ENGLISH_PLACEHOLDERS,
  'en-FR': ENGLISH_PLACEHOLDERS,
  'en-NL': ENGLISH_PLACEHOLDERS,
  'en-DE': ENGLISH_PLACEHOLDERS,
  'es-US': ENGLISH_PLACEHOLDERS,
  'de-DE': {
    REGION: 'REGION',
    PRODUCT: 'PRODUKT',
    LOCATION: {
      us: 'BUNDESSTAAT',
      eu: 'LAND',
    },
    EMAIL: 'MEINE E-MAIL',
  },
  'fr-FR': {
    REGION: 'RÉGION',
    PRODUCT: 'PRODUIT',
    LOCATION: {
      us: 'ÉTAT',
      eu: 'PAYS',
    },
    EMAIL: 'MON EMAIL',
  },
  'nl-NL': {
    REGION: 'REGIO',
    PRODUCT: 'PRODUCT',
    LOCATION: {
      us: 'STAAT',
      eu: 'LAND',
    },
    EMAIL: 'MIJN E-MAIL',
  },
} as Record<Locale, Placeholder>;

const EN_REGIONS = {
  eu: 'Europe',
  us: 'United States',
} as Record<Region, string>;

export const REGION = {
  'en-US': EN_REGIONS,
  'en-DE': EN_REGIONS,
  'en-GB': EN_REGIONS,
  'en-NL': EN_REGIONS,
  'en-FR': EN_REGIONS,
  'nl-NL': {
    eu: 'Europa',
    us: 'VS',
  },
  'de-DE': {
    eu: 'Europa',
    us: 'USA',
  },
  'fr-FR': {
    eu: 'Europe',
    us: 'États-Unis',
  },
} as Record<Locale, Record<Region, string>>;

export const US_STATES = {
  al: 'Alabama',
  ak: 'Alaska',
  az: 'Arizona',
  ar: 'Arkansas',
  ca: 'California',
  co: 'Colorado',
  ct: 'Connecticut',
  de: 'Delaware',
  dc: 'District of Columbia',
  fl: 'Florida',
  ga: 'Georgia',
  hi: 'Hawaii',
  id: 'Idaho',
  il: 'Illinois',
  in: 'Indiana',
  ia: 'Iowa',
  ks: 'Kansas',
  ky: 'Kentucky',
  la: 'Louisiana',
  me: 'Maine',
  md: 'Maryland',
  ma: 'Massachusetts',
  mi: 'Michigan',
  mn: 'Minnesota',
  ms: 'Mississippi',
  mo: 'Missouri',
  mt: 'Montana',
  ne: 'Nebraska',
  nv: 'Nevada',
  nh: 'New Hampshire',
  nj: 'New Jersey',
  nm: 'New Mexico',
  ny: 'New York',
  nc: 'North Carolina',
  nd: 'North Dakota',
  oh: 'Ohio',
  ok: 'Oklahoma',
  or: 'Oregon',
  pa: 'Pennsylvania',
  ri: 'Rhode Island',
  sc: 'South Carolina',
  sd: 'South Dakota',
  tn: 'Tennessee',
  tx: 'Texas',
  ut: 'Utah',
  vt: 'Vermont',
  va: 'Virginia',
  wa: 'Washington',
  wv: 'West Virginia',
  wi: 'Wisconsin',
  wy: 'Wyoming',
} as const;

const EuCountriesEN = {
  at: 'austria',
  be: 'belgium',
  bg: 'bulgaria',
  hr: 'croatia',
  cy: 'cyprus',
  cz: 'czech republic',
  dk: 'denmark',
  ee: 'estonia',
  fi: 'finland',
  fr: 'france',
  de: 'germany',
  gr: 'greece',
  hu: 'hungary',
  ie: 'ireland',
  it: 'italy',
  lv: 'latvia',
  lt: 'lithuania',
  lu: 'luxembourg',
  mt: 'malta',
  nl: 'netherlands',
  pl: 'poland',
  pt: 'portugal',
  ro: 'romania',
  sk: 'slovakia',
  si: 'slovenia',
  es: 'spain',
  se: 'sweden',
  gb: 'united kingdom',
};

const EuCountriesFR = {
  at: 'autriche',
  be: 'belgique',
  bg: 'bulgarie',
  hr: 'croatie',
  cy: 'chypre',
  cz: 'république tchèque',
  dk: 'danemark',
  ee: 'estonie',
  fi: 'finlande',
  fr: 'france',
  de: 'allemagne',
  gr: 'grèce',
  hu: 'hongrie',
  ie: 'irlande',
  it: 'italie',
  lv: 'lettonie',
  lt: 'lituanie',
  lu: 'luxembourg',
  mt: 'malte',
  nl: 'pays-bas',
  pl: 'pologne',
  pt: 'portugal',
  ro: 'roumanie',
  sk: 'slovaquie',
  si: 'slovénie',
  es: 'espagne',
  se: 'suède',
  gb: 'royaume-uni',
};

const EuCountriesNL = {
  at: 'oostenrijk',
  be: 'belgië',
  bg: 'bulgarije',
  hr: 'kroatië',
  cy: 'cyprus',
  cz: 'tsjechië',
  dk: 'denemarken',
  ee: 'estland',
  fi: 'finland',
  fr: 'frankrijk',
  de: 'duitsland',
  gr: 'griekenland',
  hu: 'hongarije',
  ie: 'ierland',
  it: 'italië',
  lv: 'letland',
  lt: 'litouwen',
  lu: 'luxemburg',
  mt: 'malta',
  nl: 'nederland',
  pl: 'polen',
  pt: 'portugal',
  ro: 'roemenië',
  sk: 'slowakije',
  si: 'slovenië',
  es: 'spanje',
  se: 'zweden',
  gb: 'verenigd koninkrijk',
};

const EuCountriesDE = {
  at: 'österreich',
  be: 'belgien',
  bg: 'bulgarien',
  hr: 'kroatien',
  cy: 'zypern',
  cz: 'tschechien',
  dk: 'dänemark',
  ee: 'estland',
  fi: 'finnland',
  fr: 'frankreich',
  de: 'deutschland',
  gr: 'griechenland',
  hu: 'ungarn',
  ie: 'irland',
  it: 'italien',
  lv: 'lettland',
  lt: 'litauen',
  lu: 'luxemburg',
  mt: 'malta',
  nl: 'niederlande',
  pl: 'polen',
  pt: 'portugal',
  ro: 'rumänien',
  sk: 'slowakei',
  si: 'slowenien',
  es: 'spanien',
  se: 'schweden',
  gb: 'vereinigtes königreich',
};

export const TRANSLATED_EU_COUNTRIES = {
  'en-US': EuCountriesEN,
  'en-GB': EuCountriesEN,
  'en-FR': EuCountriesEN,
  'en-NL': EuCountriesEN,
  'en-DE': EuCountriesEN,
  'es-US': EuCountriesEN,
  'de-DE': EuCountriesDE,
  'fr-FR': EuCountriesFR,
  'nl-NL': EuCountriesNL,
} as Record<Locale, Record<keyof typeof EuCountriesEN, string>>;

export const TRANSLATED_SUBMIT_BUTTON = {
  'en-US': 'let me know',
  'en-GB': 'let me know',
  'en-FR': 'let me know',
  'en-NL': 'let me know',
  'en-DE': 'let me know',
  'es-US': 'let me know',
  'de-DE': 'Sag mir bescheid',
  'fr-FR': 'Faites-moi signe',
  'nl-NL': 'Houd me op de hoogte',
} as Record<Locale, string>;

export const FORM_SUBMIT_MESSAGE = {
  'en-US': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'es-US': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'en-NL': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'en-DE': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'en-GB': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'en-FR': {
    title: "You're in!",
    message: 'We’ll notify you as soon as Lemonade gets to ',
    tell_your_friends: 'Tell your friends',
    error: 'Something went wrong, please try again later',
  },
  'nl-NL': {
    title: 'Gelukt!',
    message: 'We laten het je weten als we naar België komen.',
    tell_your_friends: 'Deel met je vrienden',
    error: 'Er is iets misgegaan. Probeer het later opnieuw',
  },
  'de-DE': {
    title: 'Du bist dabei!',
    message: 'Wir werden dich benachrichtigen, sobald Lemonade in Belgien verfügbar ist',
    tell_your_friends: 'Sag’s deinen Freunden',
    error: 'Etwas ist schiefgelaufen, bitte versuche es später erneut',
  },
  'fr-FR': {
    title: "C'est noté !",
    message: 'Nous vous informerons dès que Lemonade sera disponible en Autriche.',
    tell_your_friends: 'Parlez-en à vos amis',
    error: 'Quelque chose s’est mal passé, veuillez réessayer plus tard',
  },
} as Record<Locale, Record<string, string>>;
